import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import NewsletterForm from "@/components/NewsletterForm";
import { faqs, siteInfo } from "@/mocks/site";

const contactPoints = [
  { icon: "ri-mail-line", label: "内容与投稿咨询", value: siteInfo.email },
  { icon: "ri-time-line", label: "回复时间", value: "工作日 09:00 - 18:00" },
  { icon: "ri-message-3-line", label: "建议与反馈", value: "来信请注明话题，方便我们归类整理" },
];

export default function Faq() {
  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-16 md:pt-[72px]">
        {/* head */}
        <section className="bg-primary-950">
          <div className="mx-auto max-w-4xl px-4 py-14 text-center md:px-6 md:py-20">
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/10 px-4 py-1.5 text-sm font-medium text-accent-100 ring-1 ring-background-50/20">
              常见问题
            </span>
            <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              关于 51瓜网 的问题，都在这里
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-background-100/75 md:text-base">
              第一次来不知道从哪开始？想了解网站的定位与使用方式？下面整理了一些常见疑问，希望能帮到你。
            </p>
          </div>
        </section>

        {/* faq body */}
        <section className="mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            <aside className="md:col-span-1">
              <div className="md:sticky md:top-24">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-questionnaire-line text-primary-600"></i>
                  问题目录
                </h2>
                <ul className="mt-4 space-y-2 text-sm">
                  {faqs.map((f, i) => (
                    <li key={f.q}>
                      <a
                        href={`#faq-${i}`}
                        className="line-clamp-2 text-foreground-600 transition-colors hover:text-primary-700"
                      >
                        {f.q}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className="md:col-span-3">
              {faqs.map((f, i) => (
                <FaqCard key={f.q} q={f.q} a={f.a} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* contact */}
        <section className="bg-accent-50">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-xs font-semibold text-foreground-700">
                  仍然有疑问？
                </span>
                <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                  欢迎随时联系我们
                </h2>
                <p className="mt-3 max-w-lg text-sm leading-7 text-foreground-600">
                  如果上面的问题没有覆盖到你的困惑，欢迎通过邮箱与我们沟通。也可以先到资料库逛逛，说不定已经有人整理过你想了解的内容。
                </p>
                <Link
                  to="/library"
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-compass-3-line"></i>
                  去资料库看看
                </Link>
              </div>
              <div className="space-y-3">
                {contactPoints.map((c) => (
                  <div
                    key={c.icon}
                    className="flex items-start gap-4 rounded-2xl border border-background-200 bg-background-50 p-5"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                      <i className={`${c.icon} text-xl`}></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{c.label}</p>
                      <p className="mt-1 text-sm font-medium text-foreground-900">{c.value}</p>
                    </div>
                  </div>
                ))}
                <div className="rounded-2xl bg-background-50 p-5">
                  <p className="mb-3 text-sm font-semibold text-foreground-900">顺便订阅一份瓜讯？</p>
                  <NewsletterForm variant="light" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function FaqCard({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div
      id={`faq-${index}`}
      className="mb-4 scroll-mt-24 overflow-hidden rounded-2xl border border-background-200 bg-background-50"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-6 py-5 text-left"
      >
        <span className="flex items-center gap-3 font-heading text-base font-bold text-foreground-900 md:text-lg">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-sm font-black text-accent-800">
            {String(index + 1).padStart(2, "0")}
          </span>
          {q}
        </span>
        <i
          className={`shrink-0 text-lg text-primary-600 transition-transform ${open ? "rotate-180" : ""} ${
            open ? "ri-subtract-line" : "ri-add-line"
          }`}
        ></i>
      </button>
      {open && (
        <p className="border-t border-background-100 px-6 py-5 pl-[52px] text-sm leading-7 text-foreground-600">
          {a}
        </p>
      )}
    </div>
  );
}